import { useParams } from "react-router-dom";
import { ErrorState } from "@common/index";
import { NotFoundPage } from "@customer/ErrorPages/NotFoundPage";
import { SortFilterSidebar } from "@customer/Search/SortFilterSidebar";
import { SortFilterBar } from "@customer/Search/SortFilterBar";
import { SortSheet } from "@customer/Search/SortSheet";
import { FilterSheet } from "@customer/Search/FilterSheet";
import { useBrowseSubcategory } from "./hooks/useBrowseSubcategory";
import { SubcategoryBreadcrumb } from "./components/SubcategoryBreadcrumb";
import { FeaturedProductBanner } from "./components/FeaturedProductBanner";
import { ProductGrid } from "./components/ProductGrid";

// /c/browse/:categoryId — the category page, with the #226 sort/filter
// surface since #401 B4 (desktop rail, mobile bar + sheets). The category
// itself is fixed by the route, so both panels hide their category chips.
//
// The route boundary. A param that is not a positive integer is not a
// category: it gets the same not-found page any unknown URL gets, and the
// hook below is never handed NaN (whose `!==` self-inequality would re-run
// its render-phase reset forever). Split from the page so the hook is only
// ever called with an id it can use.
export const BrowseSubcategory = () => {
    const { categoryId: categoryIdParam } = useParams<{ categoryId: string }>();
    const categoryId = Number(categoryIdParam);

    if (!Number.isInteger(categoryId) || categoryId <= 0) {
        return <NotFoundPage />;
    }

    return <BrowseSubcategoryPage categoryId={categoryId} />;
};

interface BrowseSubcategoryPageProps {
    categoryId: number;
}

const BrowseSubcategoryPage = ({ categoryId }: BrowseSubcategoryPageProps) => {
    const {
        categoryChain,
        currentCategory,
        featuredProduct,
        productPage,
        isLoadingProducts,
        hasError,
        retry,
        pageNumber,
        setPage,
    } = useBrowseSubcategory(categoryId);

    return (
        <div data-testid="browse-subcategory" className="bg-base-100">
            <div className="max-w-7xl mx-auto flex">
                <SortFilterSidebar showCategories={false} />
                <div className="flex-1 min-w-0">
                    <SubcategoryBreadcrumb chain={categoryChain} />

                    {featuredProduct && (
                        <FeaturedProductBanner
                            product={featuredProduct}
                            categoryName={currentCategory?.name ?? ""}
                        />
                    )}

                    {hasError ? (
                        <ErrorState onRetry={retry} />
                    ) : (
                        <ProductGrid
                            products={productPage.items}
                            pageNumber={pageNumber}
                            totalPages={productPage.totalPages}
                            isLoading={isLoadingProducts}
                            onPageChange={setPage}
                        />
                    )}
                </div>
            </div>
            <SortFilterBar />
            <SortSheet />
            <FilterSheet showCategories={false} />
        </div>
    );
};
